import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Checkbox from "@mui/material/Checkbox";
import { DataGrid } from "@mui/x-data-grid";
import { exportFile } from "../services/global/global";
import { toast } from "./AppToast";
import { handleExportFromBase64 } from "../utils/ExportTableData";
import ValidationErrorModal from "../components/ValidationErrorModal";
import { useValidationError } from "../hooks/useValidationError";
import FileUploadModal from "../components/FileUploadModal";
import { useFileUpload } from "../hooks/useFileUpload";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import FilterListIcon from "@mui/icons-material/FilterList";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";

import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";





// 🔥 Better text width estimator (pixels)
const estimateTextWidth = (value) => {
  if (value === null || value === undefined) return 0;

  const str = String(value);
  const avgCharWidth = 8.5; //
  const raw = str.length * avgCharWidth;

  return Math.min(raw, 800); 
};



const STATUS_TONES = [
  { match: /(cancel|reject|fail|error|block|inactive|void)/i, tone: "danger" },
  { match: /(pending|hold|partial|draft|await|review)/i, tone: "warning" },
  { match: /(complete|deliver|receiv|approv|success|close|done|active|invoic)/i, tone: "success" },
  { match: /(process|transit|progress|pick|pack|ship|dispatch)/i, tone: "info" },
  { match: /(new|open|created)/i, tone: "primary" },
];

const statusTone = (value) => {
  const text = String(value ?? "");
  const hit = STATUS_TONES.find(({ match }) => match.test(text));
  return hit ? hit.tone : "neutral";
};

const isStatusColumn = (col) => /status$/i.test(col?.field || "") || /^status$/i.test(col?.headerName || "");

const renderStatusChip = (params) => {
  const value = params?.value;
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "object") return String(value?.label ?? value?.name ?? "");
  return <span className={`cdt-status cdt-status-${statusTone(value)}`}>{String(value)}</span>;
};

// Auto size columns based on header + sample rows
const autoSizeColumns = (cols, rows) => {
  const safeRows = Array.isArray(rows) ? rows.slice(0, 200) : []; // limit for performance


  const wideFields = new Set([
    "soNumber",
    "source",
    "shipmentHistory",
    "items",
   
  ]);

  return (Array.isArray(cols) ? cols : []).map((col) => {
  
    if (col.width || col.flex) return col;

    const headerWidth = estimateTextWidth(col.headerName || col.field);

    let maxDataWidth = 0;
    for (const r of safeRows) {
      const cellVal = r?.[col.field];
      const w = estimateTextWidth(cellVal);
      if (w > maxDataWidth) maxDataWidth = w;
    }

    const base = Math.max(headerWidth, maxDataWidth);
    const padding = 40; 
    const rawWidth = base + padding;

   
    const min = wideFields.has(col.field) ? 160 : 80;
    const max = wideFields.has(col.field) ? 500 : 320;

    const finalWidth = Math.min(Math.max(rawWidth, min), max);

    return {
      ...col,
      width: finalWidth,
    };
  });
};


const SYNC_WMS_COOLDOWN_MS = 60 * 1000;
const syncWmsLocks = new Map();
const syncWmsLockListeners = new Set();

const syncWmsKeyFor = (id, exportImportPath) =>
  [id, exportImportPath].filter(Boolean).join("|") || "sync-wms";

const getSyncWmsLock = (key) => syncWmsLocks.get(key) || 0;

const setSyncWmsLock = (key, until) => {
  syncWmsLocks.set(key, until);
  syncWmsLockListeners.forEach((listener) => listener(key, until));
};

export default function CommonDataTable({
  id = "",
  allPossibleColumns = [],
  initialVisible = [],
  initialRows,
  height = 500,
  rowsPerPageOptions = [10, 25, 50, 100],
  forItems,
  onDataRefresh, // parent can trigger refresh
  checkboxSelection,
  onImport, // optional override
  onExport, // optional override
  forInventory,
  exportImportPath,
  exportQueryParams,
  sampleFileUrl, // Optional: custom sample file URL
  sampleFileName, // Optional: custom sample file name
  // New props for create button
  showCreateButton = false,
  createButtonText = "Create New",
  createButtonIcon = "bi-plus-lg",
  onCreateClick,
  createButtonVariant = "contained",
  createButtonColor = "primary",
  enableDateRangeFilter = false,
  onDateRangeChange,
  defaultDateRange = { start: "", end: "" },
  forItemsexport = false,
  enableImportButton = true,
  enableExportButton = true,
  getRowClassName,


  // 🔗 hyperlink support
  linkColumns = {}, // e.g. { asnNumbers: { entity:'ASN', parseValues:(v,row)=>[...] } }
  onLinkOpen, // ({field,value,row,entity}) => void
  page,
  pageSize = 10,
  rowCount,
  onPageChange,
  onPageSizeChange,


  // autoRefreshIntervalMs = 2 * 60 * 60 * 1000, // 2 hours

  autoRefreshIntervalMs = 15 * 60 * 1000, // 15 minutes


  onManualRefresh,

  /** When `checkboxSelection` is on, notifies parent of current selection: `{ ids, rows }` from `displayedRows`. */
  onSelectionChange,

  /** Shows a Sync WMS action immediately before Last Refreshed. */
  showSyncWms = false,
  onSyncWms,

  /** Per-column Filter inputs under each header (Inventory Visibility style). */
  enableInlineColumnFilters = false,


}) {

  const { t } = useTranslation();


  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState(initialRows || []);
  const [displayedRows, setDisplayedRows] = React.useState(initialRows || []);
  const [selectedRows, setSelectedRows] = React.useState([]);


  const [lastRefreshed, setLastRefreshed] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const syncKey = syncWmsKeyFor(id, exportImportPath);
  const [syncing, setSyncing] = React.useState(false);
  const [syncLockedUntil, setSyncLockedUntil] = React.useState(() => getSyncWmsLock(syncKey));
  const syncCoolingDown = syncLockedUntil > Date.now();

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState(() => {
    const cols = Array.isArray(allPossibleColumns) ? allPossibleColumns : [];
    const visible = Array.isArray(initialVisible) ? initialVisible : [];
    return Object.fromEntries(cols.map((col) => [col.field, visible.includes(col.field)]));
  });

  // Filters state
  const [filters, setFilters] = React.useState([]); // array of { id, columnId, operator, value }
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [inlineColumnFilters, setInlineColumnFilters] = React.useState({});
  const inlineFilterTimerRef = React.useRef(null);

  // Column order
  const [columnOrder, setColumnOrder] = React.useState(
    (Array.isArray(allPossibleColumns) ? allPossibleColumns : []).map((col) => col.field)
  );

  // Drag state
  const [draggedColumn, setDraggedColumn] = React.useState(null);


  const [clientPaginationModel, setClientPaginationModel] = React.useState({
    page: 0,
    pageSize: pageSize ?? 10,
  });



  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);

      // 1) If parent explicitly passed onManualRefresh → use it first
      if (typeof onManualRefresh === "function") {
        await onManualRefresh({
          filters: filters || [],
          page: page ?? 0,
          pageSize: pageSize ?? 10,
        });
      }
      // 2) Fallback — parent’s existing onDataRefresh
      else if (typeof onDataRefresh === "function") {
        await onDataRefresh({
          filters: filters || [],
          page: page ?? 0,
          pageSize: pageSize ?? 10,
        });
      }

      setLastRefreshed(new Date().toISOString());
    } catch (err) {
      console.error("Refresh failed:", err);
      toast.error(err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    const listener = (key, until) => {
      if (key === syncKey) setSyncLockedUntil(until);
    };
    syncWmsLockListeners.add(listener);
    setSyncLockedUntil(getSyncWmsLock(syncKey));
    return () => syncWmsLockListeners.delete(listener);
  }, [syncKey]);

  React.useEffect(() => {
    const remaining = syncLockedUntil - Date.now();
    if (remaining <= 0) return undefined;
    const timer = window.setTimeout(() => setSyncLockedUntil(0), remaining);
    return () => window.clearTimeout(timer);
  }, [syncLockedUntil]);

  const handleSyncWms = async () => {
    if (getSyncWmsLock(syncKey) > Date.now()) {
      toast.info(t("commonTable.syncWmsWait"));
      return;
    }

    try {
      setSyncing(true);
      if (typeof onSyncWms !== "function") {
        toast.info(t("commonTable.syncWmsUnavailable"));
        return;
      }
      await onSyncWms({
        ids: selectedRows?.values || [],
        filters: filters || [],
        page: page ?? 0,
        pageSize: pageSize ?? 10,
      });
      setSyncWmsLock(syncKey, Date.now() + SYNC_WMS_COOLDOWN_MS);
      toast.success(t("commonTable.syncWmsSuccess"));
    } catch (err) {
      console.error("WMS sync failed:", err);
      toast.error(err?.message || t("commonTable.syncWmsFailed"));
    } finally {
      setSyncing(false);
    }
  };


  // set lastRefreshed when rows change (so manual/remote updates update stamp)
  React.useEffect(() => {
    if (Array.isArray(rows) && rows.length >= 0) {
      // only set if not already set (or you want to update each time rows change)
      setLastRefreshed(new Date().toISOString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const manualRefreshRef = React.useRef(handleManualRefresh);
  manualRefreshRef.current = handleManualRefresh;

  // auto refresh interval
  React.useEffect(() => {
    const intervalMs = typeof autoRefreshIntervalMs === "number" ? autoRefreshIntervalMs : 2 * 60 * 60 * 1000;
    const id = setInterval(() => {
      manualRefreshRef.current();
    }, intervalMs);

    return () => clearInterval(id);
  }, [autoRefreshIntervalMs]);


  // File upload hook
  const { showUploadModal, openUploadModal, closeUploadModal, handleFileUpload: uploadHandler, handleDownloadSample, computedSampleFileName } =
    useFileUpload({
      uploadPath: exportImportPath,
      sampleFileUrl,
      sampleFileName,
      onSuccess: (result) => {
        if (result.data && Array.isArray(result.data)) {
          setRows(result.data);
          setDisplayedRows(result.data); 
        }
        // if (onDataRefresh) {
        //   setTimeout(() => {
        //     onDataRefresh();
        //   }, 500);
        // }
      },
      onValidationError: (errors) => {
        handleValidationError({ isValidationError: true, validationData: errors });
      },
    });

  // Keep rows synced
  React.useEffect(() => {
    setRows(initialRows || []);
  }, [initialRows]);

  // Compute displayed rows
  React.useEffect(() => {
    if (!filters || filters.length === 0) {
      setDisplayedRows(rows);
      return;
    }
    const filtered = rows.filter((r) => matchesAllFilters(r, filters));
   
    setDisplayedRows(filtered);
  }, [rows, filters]);

  // Keep visibility/order synced with parent props
  React.useEffect(() => {
    const cols = Array.isArray(allPossibleColumns) ? allPossibleColumns : [];
    const visible = Array.isArray(initialVisible) ? initialVisible : [];
    setColumnVisibilityModel(Object.fromEntries(cols.map((col) => [col.field, visible.includes(col.field)])));
    setColumnOrder(cols.map((col) => col.field));
  }, [initialVisible, allPossibleColumns]);

  // Load saved prefs
  React.useEffect(() => {
    if (id && Array.isArray(allPossibleColumns) && allPossibleColumns.length > 0) {
      try {
        const savedOrder = localStorage.getItem(`columnOrder_${id}`);
        if (savedOrder) {
          const parsedOrder = JSON.parse(savedOrder);
          const currentFields = new Set(allPossibleColumns.map((col) => col.field));
          const validOrder = Array.isArray(parsedOrder) ? parsedOrder.filter((f) => currentFields.has(f)) : [];
          const missingFields = allPossibleColumns.map((col) => col.field).filter((field) => !validOrder.includes(field));
          setColumnOrder([...validOrder, ...missingFields]);
        } else {
          setColumnOrder(allPossibleColumns.map((col) => col.field));
        }

        const savedVisibility = localStorage.getItem(`columnVisibility_${id}`);
        if (savedVisibility) {
          const parsedVisibility = JSON.parse(savedVisibility) || {};
          setColumnVisibilityModel((prev) => ({ ...prev, ...parsedVisibility }));
        }
      } catch (error) {
        console.warn("Could not load column preferences from localStorage:", error);
        setColumnOrder(allPossibleColumns.map((col) => col.field));
      }
    }
  }, [id, allPossibleColumns]);

  // Save prefs
  React.useEffect(() => {
    if (id) {
      try {
        localStorage.setItem(`columnOrder_${id}`, JSON.stringify(columnOrder));
      } catch (error) {
        console.warn("Could not save column order to localStorage:", error);
      }
    }
  }, [columnOrder, id]);

  React.useEffect(() => {
    if (id) {
      try {
        localStorage.setItem(`columnVisibility_${id}`, JSON.stringify(columnVisibilityModel));
      } catch (error) {
        console.warn("Could not save column visibility to localStorage:", error);
      }
    }
  }, [columnVisibilityModel, id]);

  // 🔗 Helper: render links for a cell if configured
  const buildLinkedCellRenderer = React.useCallback(
    (col, origRenderCell) => {
      const linkCfg = linkColumns?.[col.field];
      if (!linkCfg) return origRenderCell;

      const parseValues = (value, row) => {
        if (typeof linkCfg?.parseValues === "function") {
          return linkCfg.parseValues(value, row);
        }
        if (Array.isArray(value)) return value;
        const str = String(value ?? "");
        if (!str.trim()) return [];
        // default split by comma
        return str
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      };

      return (params) => {
        const value = params?.value;
        const list = parseValues(value, params?.row);
        if (!list || list.length === 0) {
          return origRenderCell ? origRenderCell(params) : value ?? "";
        }

        const handleClick = (e, v) => {
          e.stopPropagation();
          if (typeof onLinkOpen === "function") {
            onLinkOpen({
              field: col.field,
              value: v,
              row: params?.row,
              entity: linkCfg?.entity || undefined,
            });
          }
        };

        return (
          <Box
            sx={{
              display: "block",
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              whiteSpace: "nowrap",       // 👈 no wrap, single line
              scrollbarWidth: "thin",     // Firefox
              "&::-webkit-scrollbar": { height: 6 },           // Chrome/Edge
              "&::-webkit-scrollbar-thumb": { borderRadius: 3, background: "#2b3991" },
            }}
          >
            {list.map((v, idx) => (
              <React.Fragment key={`${col.field}-${v}-${idx}`}>
                <Link
                  component="button"
                  underline={linkCfg?.underline === "always" ? "always" : "hover"}
                  onClick={(e) => handleClick(e, v)}
                  sx={{
                    p: 0,
                    m: 0,
                    fontSize: "0.875rem",
                    display: "inline-flex",
                    alignItems: "center",
                    ...(linkCfg?.underline === "always"
                      ? { color: "primary.main" }
                      : {}),
                  }}
                >
                  {v}
                </Link>
                {idx < list.length - 1 ? <span>,&nbsp;</span> : null}
              </React.Fragment>
            ))}
          </Box>
        );
      };

    },
    [linkColumns, onLinkOpen]
  );

  // Header renderer with DnD
  const moveColumn = React.useCallback(
    (draggedField, targetField) => {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const draggedIndex = newOrder.indexOf(draggedField);
        const targetIndex = newOrder.indexOf(targetField);
        if (draggedIndex === -1 || targetIndex === -1) return prevOrder;

        const [removed] = newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, removed);

        const draggedCol = (allPossibleColumns || []).find((c) => c.field === draggedField);
        toast.success(`Moved "${draggedCol?.headerName || draggedField}" column`, {
          autoClose: 1500,
          hideProgressBar: true,
        });

        return newOrder;
      });
    },
    [allPossibleColumns]
  );

  const notifyFiltersChanged = React.useCallback(
    (nextFilters) => {
      if (typeof onDataRefresh !== "function") return;
      try {
        onDataRefresh({
          filters: nextFilters || [],
          page: 0,
          pageSize: pageSize ?? 10,
        });
      } catch (e) {
        console.error("onDataRefresh threw:", e);
      }
    },
    [onDataRefresh, pageSize]
  );

  const buildInlineFilterRules = React.useCallback((values = {}) => {
    return Object.entries(values)
      .filter(([, value]) => value != null && String(value).trim() !== "")
      .map(([columnId, value]) => ({
        id: `inline-${columnId}`,
        columnId,
        operator: "contains",
        value: String(value).trim(),
        source: "inline",
      }));
  }, []);

  const applyInlineColumnFilters = React.useCallback(
    (values) => {
      setFilters((prev) => {
        const modalOnly = (prev || []).filter((rule) => rule?.source !== "inline");
        const nextFilters = [...modalOnly, ...buildInlineFilterRules(values)];
        notifyFiltersChanged(nextFilters);
        return nextFilters;
      });
    },
    [buildInlineFilterRules, notifyFiltersChanged]
  );

  const updateInlineColumnFilter = React.useCallback(
    (field, value) => {
      setInlineColumnFilters((prev) => {
        const next = { ...prev, [field]: value };
        if (inlineFilterTimerRef.current) clearTimeout(inlineFilterTimerRef.current);
        inlineFilterTimerRef.current = setTimeout(() => {
          applyInlineColumnFilters(next);
        }, 350);
        return next;
      });
    },
    [applyInlineColumnFilters]
  );

  React.useEffect(
    () => () => {
      if (inlineFilterTimerRef.current) clearTimeout(inlineFilterTimerRef.current);
    },
    []
  );

  const defaultDraggableHeader = (col) => {
    const handleDragStart = (e) => {
      setDraggedColumn(col.field);
      e.dataTransfer.setData("text/plain", col.field);
      e.dataTransfer.effectAllowed = "move";
      e.target.style.opacity = "0.6";
    };

    const handleDragEnd = (e) => {
      setDraggedColumn(null);
      e.target.style.opacity = "1";
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e) => {
      e.preventDefault();
      const draggedField = e.dataTransfer.getData("text/plain");
      const targetField = col.field;
      if (draggedField && draggedField !== targetField) {
        moveColumn(draggedField, targetField);
      }
    };

    const showInlineFilter =
      enableInlineColumnFilters &&
      col.filterable !== false &&
      col.field &&
      !String(col.field).startsWith("__");

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: showInlineFilter ? "4px" : 0,
          boxSizing: "border-box",
          px: 0.5,
          py: showInlineFilter ? 0.5 : 0,
        }}
      >
        <Box
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            cursor: "grab",
            userSelect: "none",
            padding: "2px 4px",
            borderRadius: "6px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.18s ease, color 0.18s ease",
            backgroundColor: draggedColumn === col.field ? "rgba(30, 113, 187, 0.1)" : "transparent",
            "&:hover": {
              color: "#1e71bb",
            },
            "&:active": { cursor: "grabbing" },
          }}
          title={`Drag to reorder: ${col.headerName}`}
        >
          <Box className="cdt-header-label">{col.headerName}</Box>
        </Box>
        {showInlineFilter && (
          <Box
            component="input"
            type="text"
            className="cdt-col-filter"
            placeholder=""
            value={inlineColumnFilters[col.field] ?? ""}
            onChange={(e) => updateInlineColumnFilter(col.field, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
          />
        )}
      </Box>
    );
  };

  // Ordered + enhanced columns
  const orderedColumns = React.useMemo(() => {
    // visible rows (filters lagne ke baad), warna full rows
    const sourceRows =
      (displayedRows && displayedRows.length > 0) ? displayedRows : rows;

    const baseCols = Array.isArray(allPossibleColumns)
      ? autoSizeColumns(allPossibleColumns, sourceRows)
      : [];

    const enhanceColumn = (col) => {
      const origRenderCell =
        col.renderCell || (isStatusColumn(col) && !linkColumns?.[col.field] ? renderStatusChip : undefined);
      const maybeLinked = buildLinkedCellRenderer(col, origRenderCell);
      return {
        ...col,
        renderCell: maybeLinked,
        renderHeader: () => defaultDraggableHeader(col),
      };
    };

    if (!Array.isArray(columnOrder) || !columnOrder.length || !baseCols.length) {
      return baseCols.map(enhanceColumn);
    }

    const ordered = columnOrder
      .map((fieldName) => baseCols.find((col) => col.field === fieldName))
      .filter(Boolean);

    const orderedFields = new Set(ordered.map((col) => col.field));
    const missingColumns = baseCols.filter((col) => !orderedFields.has(col.field));
    const finalColumns = [...ordered, ...missingColumns];

    return finalColumns.map(enhanceColumn);
  }, [
    columnOrder,
    allPossibleColumns,
    draggedColumn,
    buildLinkedCellRenderer,
    linkColumns,
    rows,
    displayedRows,
    enableInlineColumnFilters,
    inlineColumnFilters,
    updateInlineColumnFilter,
  ]);


  // Reset order
  const resetColumnOrder = () => {
    const defaultOrder = (allPossibleColumns || []).map((col) => col.field);
    setColumnOrder(defaultOrder);

    if (id) {
      try {
        localStorage.removeItem(`columnOrder_${id}`);
      } catch (error) {
        console.warn("Could not remove column order from localStorage:", error);
      }
    }
    toast.success("Column order reset to default");
  };

  const [rowHeight] = React.useState(42);
  const headerHeight = enableInlineColumnFilters ? 76 : 42;

  const gridHeightKK = (rows?.length || 0) >= 10 ? height : headerHeight + (rows?.length || 0) * rowHeight + 2;

  const gridHeight =
    (displayedRows?.length || 0) >= 10
      ? height
      : headerHeight + (displayedRows?.length || 0) * rowHeight + 2;

  // 📤 Export
  const [dateRange, setDateRange] = React.useState(enableDateRangeFilter ? defaultDateRange : { start: "", end: "" });

  const handleExport = async () => {
    try {
      if (typeof onExport === "function") {
        await onExport(selectedRows);
        return;
      }
      const data = await exportFile(exportImportPath, selectedRows, dateRange, exportQueryParams);
      // handleExportFromBase64(data, `${exportImportPath}_export.csv`);
       toast.success("Your file is being generated. Track its progress and download it under: Tools > Excel Imports/Exports");
      
    } catch (err) {
      console.error("Export error:", err);
      toast.error(err?.message || "Export failed");
    }
  };

  // Validation error handling
  const { validationErrors, showErrorModal, hideValidationErrors, handleValidationError } = useValidationError();

  // Column search
  const [columnSearchTerm, setColumnSearchTerm] = React.useState("");

  const filteredColumns = React.useMemo(() => {
    const cols = Array.isArray(allPossibleColumns) ? allPossibleColumns : [];
    if (!columnSearchTerm.trim()) return cols;
    return cols.filter(
      (col) =>
        (col.headerName || "").toLowerCase().includes(columnSearchTerm.toLowerCase()) ||
        (col.field || "").toLowerCase().includes(columnSearchTerm.toLowerCase())
    );
  }, [allPossibleColumns, columnSearchTerm]);

  const preferenceColumns = React.useMemo(() => {
    const byField = new Map(filteredColumns.map((col) => [col.field, col]));
    const ordered = columnOrder.map((field) => byField.get(field)).filter(Boolean);
    const seen = new Set(ordered.map((col) => col.field));
    return [...ordered, ...filteredColumns.filter((col) => !seen.has(col.field))];
  }, [filteredColumns, columnOrder]);

  const visibleColumnCount = Object.values(columnVisibilityModel).filter(Boolean).length;

  // Clear search when dialog closes
  const handleCloseDialog = () => {
    setOpen(false);
    setColumnSearchTerm("");
  };

  /* -------------------- Filter utilities & UI -------------------- */
  const genId = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const operatorsByType = {
    text: [
      { value: "contains", label: "Contains" },
      { value: "equals", label: "Equals" },
      { value: "startsWith", label: "Starts with" },
      { value: "endsWith", label: "Ends with" },
    ],
    number: [
      { value: "=", label: "=" },
      { value: ">", label: ">" },
      { value: "<", label: "<" },
      { value: ">=", label: ">=" },
      { value: "<=", label: "<=" },
    ],
    date: [
      { value: "between", label: "Between" },
      { value: "=", label: "=" },
      { value: ">=", label: ">=" },
      { value: "<=", label: "<=" },
    ],
  };

  const detectColumnType = (col) => {
    if (!col) return "text";
    if (col.type) return col.type; // prefer explicit
    const f = (col.field || "").toLowerCase();
    if (f.includes("date")) return "date";
    if (f.includes("qty") || f.includes("count") || f.includes("amount") || f.includes("price")) return "number";
    return "text";
  };

  // --- DATE PARSER HELPER (ADD THIS NEAR TOP) ---
  // ---------- date parsing helper (paste near top of CommonDataTable file) ----------
  // ---------- helper: robust value resolver ----------
  const resolveValueForRuleOLD = (row, columnId) => {
    if (!columnId) return undefined;

    // 1) direct property (most common)
    if (Object.prototype.hasOwnProperty.call(row, columnId)) return row[columnId];

    // 2) try dot-path on row (e.g. "purchase_order.expectedReceiptDate")
    const tryDotPath = (obj, path) => {
      if (!obj || !path) return undefined;
      const parts = path.split('.');
      let cur = obj;
      for (let p of parts) {
        if (cur == null) return undefined;
        // try property with same key
        if (Object.prototype.hasOwnProperty.call(cur, p)) {
          cur = cur[p];
          continue;
        }
        // try camelCase variant (e.g. purchase_order -> purchaseOrder)
        const camel = p.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase());
        if (Object.prototype.hasOwnProperty.call(cur, camel)) {
          cur = cur[camel];
          continue;
        }
        // fallback: property not found
        return undefined;
      }
      return cur;
    };

    // try on top-level row
    const fromRow = tryDotPath(row, columnId);
    if (fromRow !== undefined) return fromRow;

    // try on raw original API object if present
    const raw = row.raw || row._original || row.original || null;
    const fromRaw = tryDotPath(raw, columnId);
    if (fromRaw !== undefined) return fromRaw;

    // last resort: try common alt keys (snake <-> camel)
    const alt = columnId.replace(/\./g, '_'); // purchase_order.expected -> purchase_order_expected
    if (Object.prototype.hasOwnProperty.call(row, alt)) return row[alt];

    return undefined;
  };

  // ---------- helper: parse date robustly ----------
  // ---------- paste this above the component definition ----------
  // Put this near the top of CommonDataTable.jsx (with other helpers)
  const parseDateValueOLD = (input) => {
    if (input === null || input === undefined || input === "") return NaN;

    // If it's already a number (milliseconds)
    if (typeof input === "number") return input;

    // If it's already a Date
    if (input instanceof Date) return input.getTime();

    const s = String(input).trim();

    // ISO / RFC / Date.parse friendly formats
    const direct = Date.parse(s);
    if (!Number.isNaN(direct)) return direct;

    // Try to capture dd/mm/yyyy or dd-mm-yyyy (optionally with time after a comma or space)
    // e.g. "25/11/2025", "25-11-2025", "25/11/2025, 08:58"
    const primary = s.split(",")[0].trim(); // drop trailing time if formatted like "date, time"
    const dmy = primary.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmy) {
      const day = Number(dmy[1]), mon = Number(dmy[2]), year = Number(dmy[3]);
      return new Date(year, mon - 1, day).getTime();
    }

    // Try mm/dd/yyyy / mm-dd-yyyy fallback (less likely for you, but safe)
    const mdy = primary.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (mdy) {
      const a = Date.parse(primary);
      if (!Number.isNaN(a)) return a;
    }

    // Last attempt: search for 4-digit year and parse surrounding text
    const yearMatch = s.match(/(\d{4})/);
    if (yearMatch) {
      const idx = s.indexOf(yearMatch[1]);
      const chunk = s.slice(Math.max(0, idx - 10), idx + 10);
      const parsed = Date.parse(chunk);
      if (!Number.isNaN(parsed)) return parsed;
    }

    return NaN;
  };




  // ---------- helper: robust resolver for nested/snake/camel props ----------
  const resolveValueForRule = (row, columnId) => {
    if (!row || !columnId) return undefined;

    // direct
    if (Object.prototype.hasOwnProperty.call(row, columnId)) return row[columnId];

    // try dot-path (purchase_order.expectedReceiptDate)
    const tryDotPath = (obj, path) => {
      if (!obj || !path) return undefined;
      const parts = path.split('.');
      let cur = obj;
      for (let p of parts) {
        if (cur == null) return undefined;
        if (Object.prototype.hasOwnProperty.call(cur, p)) { cur = cur[p]; continue; }
        // try snake -> camel fallback
        const camel = p.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase());
        if (Object.prototype.hasOwnProperty.call(cur, camel)) { cur = cur[camel]; continue; }
        return undefined;
      }
      return cur;
    };

    const fromRow = tryDotPath(row, columnId);
    if (fromRow !== undefined) return fromRow;

    // try inside raw/original object
    const raw = row.raw || row._original || row.original || null;
    const fromRaw = tryDotPath(raw, columnId);
    if (fromRaw !== undefined) return fromRaw;

    // fallback: try replacing dots with underscore
    const alt = columnId.replace(/\./g, '_');
    if (Object.prototype.hasOwnProperty.call(row, alt)) return row[alt];

    return undefined;
  };

  // ---------- helper: parse many date formats to ms ----------
  const parseDateValue = (input) => {
    if (input === null || input === undefined || input === "") return NaN;
    if (typeof input === "number") return input;
    if (input instanceof Date) return input.getTime();
    const s = String(input).trim();

    // fast path: ISO / RFC
    const direct = Date.parse(s);
    if (!Number.isNaN(direct)) return direct;

    // drop trailing time after comma (e.g. "25/11/2025, 08:58")
    const primary = s.split(",")[0].trim();

    // dd/mm/yyyy or dd-mm-yyyy
    const dmy = primary.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmy) {
      const day = Number(dmy[1]), mon = Number(dmy[2]), year = Number(dmy[3]);
      return new Date(year, mon - 1, day).getTime();
    }

    // fallback: try parse on substring with 4-digit year
    const yearMatch = s.match(/(\d{4})/);
    if (yearMatch) {
      const idx = s.indexOf(yearMatch[1]);
      const chunk = s.slice(Math.max(0, idx - 10), idx + 10);
      const parsed = Date.parse(chunk);
      if (!Number.isNaN(parsed)) return parsed;
    }

    return NaN;
  };


  const matchesRule = (row, rule) => {
    if (!row || !rule) return false;
    const columnId = rule.columnId;
    const op = rule.operator;
    const rv = rule.value;

    // resolve value (handles nested paths / raw objects)
    const rawVal = resolveValueForRule(row, columnId);
    if (rawVal === undefined || rawVal === null) return false;

    // TEXT ops
    const strVal = String(rawVal).toLowerCase();
    if (op === "contains") return strVal.includes(String(rv || "").toLowerCase());
    if (op === "equals") return strVal === String(rv || "").toLowerCase();
    if (op === "startsWith") return strVal.startsWith(String(rv || "").toLowerCase());
    if (op === "endsWith") return strVal.endsWith(String(rv || "").toLowerCase());

    // NUMERIC ops
    const numVal = Number(rawVal);
    const numRv = Number(rv);
    if (!Number.isNaN(numVal) && !Number.isNaN(numRv)) {
      if (op === "=") return numVal === numRv;
      if (op === ">") return numVal > numRv;
      if (op === "<") return numVal < numRv;
      if (op === ">=") return numVal >= numRv;
      if (op === "<=") return numVal <= numRv;
    }

    // DATE ops
    if (op === "between" && Array.isArray(rv)) {
      const [startRaw, endRaw] = rv;
      if (!startRaw || !endRaw) return false;
      const rowMs = parseDateValue(rawVal);
      const startMs = parseDateValue(startRaw);
      const endMs = parseDateValue(endRaw);
      if (Number.isNaN(rowMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
      // compare by ms inclusive
      return rowMs >= startMs && rowMs <= endMs;
    }

    if (["=", ">=", "<="].includes(op) || op === "equals") {
      const rowMs = parseDateValue(rawVal);
      const compMs = parseDateValue(rv);
      if (Number.isNaN(rowMs) || Number.isNaN(compMs)) return false;
      if (op === "=" || op === "equals") {
        // treat "=" for dates as same calendar day (ignore time)
        const a = toDateKey(rowMs);
        const b = toDateKey(compMs);
        return a && b ? a === b : false;
      }
      if (op === ">=") return rowMs >= compMs;
      if (op === "<=") return rowMs <= compMs;
    }

    return false;
  };


  // ---------- helper: date-only equality (normalize to yyyy-mm-dd) ----------
  const toDateKey = (ms) => {
    if (Number.isNaN(ms)) return null;
    const d = new Date(Number(ms));
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };



  const matchesAllFilters = (row, filters = []) => {
    if (!filters || filters.length === 0) return true;
    return filters.every((rule) => matchesRule(row, rule));
  };

  function FilterModal({ open, onClose, columns = [], existingFilters = [], onApply }) {
    const columnMap = React.useMemo(() => {
      return (columns || []).reduce((acc, c) => {
        if (c?.field) acc[c.field] = c;
        return acc;
      }, {});
    }, [columns]);

    const [localFilters, setLocalFilters] = React.useState(() => (existingFilters || []).map((f) => ({ ...f })));

    React.useEffect(() => {
      setLocalFilters((existingFilters || []).map((f) => ({ ...f })));
    }, [existingFilters, open]);

    const addRule = () => {
      const first = columns[0]?.field || "";
      setLocalFilters((f) => [
        ...f,
        {
          id: genId(),
          columnId: first,
          operator: operatorsByType[detectColumnType(columns[0])]?.[0]?.value || "contains",
          value: "",
        },
      ]);
    };

    const removeRule = (id) => setLocalFilters((f) => f.filter((r) => r.id !== id));
    const updateRule = (id, patch) => setLocalFilters((f) => f.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const handleApply = () => {
      onApply && onApply(localFilters.filter((l) => l && l.columnId));
      onClose && onClose();
    };

    const handleClear = () => setLocalFilters([]);

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ className: "cdt-filters-dialog" }}
      >
        <div className="cdt-columns-head">
          <div>
            <p className="cdt-columns-kicker">Table</p>
            <h3>Filters</h3>
            <p>Narrow this list by column, condition, and value. Rules apply together.</p>
          </div>
          <button type="button" className="cdt-columns-close" onClick={onClose} aria-label="Close filters">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="cdt-filters-body">
          <div className="cdt-columns-meta">
            <span>{localFilters.length === 0 ? "No active filters" : `${localFilters.length} active`}</span>
            <button type="button" onClick={addRule}>Add filter</button>
          </div>

          {localFilters.length === 0 ? (
            <div className="cdt-filter-empty">
              <i className="bi bi-funnel" aria-hidden />
              <p>Add a filter to focus the rows you need.</p>
              <button type="button" onClick={addRule}>Add filter</button>
            </div>
          ) : (
            <div className="cdt-filter-list">
              {localFilters.map((rule, index) => {
                const col = columnMap[rule.columnId] || columns[0] || {};
                const type = detectColumnType(col);
                const ops = operatorsByType[type] || operatorsByType.text;

                return (
                  <div className="cdt-filter-card" key={rule.id}>
                    <div className="cdt-filter-card-top">
                      <span className="cdt-filter-index">{index + 1}</span>
                      <button type="button" className="cdt-filter-remove" onClick={() => removeRule(rule.id)} aria-label="Remove filter">
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                    <div className="cdt-filter-grid">
                      <TextField
                        className="cdt-filter-field"
                        select
                        label="Column"
                        value={rule.columnId}
                        onChange={(e) => {
                          const newCol = e.target.value;
                          const newType = detectColumnType(columnMap[newCol]);
                          updateRule(rule.id, {
                            columnId: newCol,
                            operator: operatorsByType[newType]?.[0]?.value || "contains",
                            value: "",
                          });
                        }}
                        size="small"
                      >
                        {columns.map((c) => (
                          <MenuItem key={c.field} value={c.field}>
                            {c.headerName || c.field}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        className="cdt-filter-field"
                        select
                        label="Condition"
                        value={rule.operator}
                        onChange={(e) => updateRule(rule.id, { operator: e.target.value })}
                        size="small"
                      >
                        {ops.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      {type === "date" ? (
                        rule.operator === "between" ? (
                          <>
                            <TextField
                              className="cdt-filter-field"
                              label="From"
                              type="date"
                              size="small"
                              value={Array.isArray(rule.value) ? rule.value[0] || "" : ""}
                              onChange={(e) => {
                                const next = Array.isArray(rule.value) ? [...rule.value] : ["", ""];
                                next[0] = e.target.value;
                                updateRule(rule.id, { value: next });
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              className="cdt-filter-field"
                              label="To"
                              type="date"
                              size="small"
                              value={Array.isArray(rule.value) ? rule.value[1] || "" : ""}
                              onChange={(e) => {
                                const next = Array.isArray(rule.value) ? [...rule.value] : ["", ""];
                                next[1] = e.target.value;
                                updateRule(rule.id, { value: next });
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </>
                        ) : (
                          <TextField
                            className="cdt-filter-field cdt-filter-span"
                            label="Value"
                            type="date"
                            size="small"
                            value={rule.value ?? ""}
                            onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                        )
                      ) : rule.operator === "between" ? (
                        <>
                          <TextField
                            className="cdt-filter-field"
                            label="From"
                            size="small"
                            value={Array.isArray(rule.value) ? rule.value[0] || "" : ""}
                            onChange={(e) => {
                              const next = Array.isArray(rule.value) ? [...rule.value] : ["", ""];
                              next[0] = e.target.value;
                              updateRule(rule.id, { value: next });
                            }}
                          />
                          <TextField
                            className="cdt-filter-field"
                            label="To"
                            size="small"
                            value={Array.isArray(rule.value) ? rule.value[1] || "" : ""}
                            onChange={(e) => {
                              const next = Array.isArray(rule.value) ? [...rule.value] : ["", ""];
                              next[1] = e.target.value;
                              updateRule(rule.id, { value: next });
                            }}
                          />
                        </>
                      ) : (
                        <TextField
                          className="cdt-filter-field cdt-filter-span"
                          label="Value"
                          size="small"
                          value={rule.value ?? ""}
                          onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cdt-columns-foot">
          <div className="cdt-columns-bulk">
            <button type="button" onClick={handleClear} disabled={localFilters.length === 0}>
              Clear
            </button>
          </div>
          <div className="cdt-columns-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" className="is-primary" onClick={handleApply}>Apply</button>
          </div>
        </div>
      </Drawer>
    );
  }


  /* -------------------- End Filter utilities & UI -------------------- */

  const openFilterModal = () => setFilterModalOpen(true);
  const closeFilterModal = () => setFilterModalOpen(false);

  const onApplyFiltersOLD = (newFilters) => {
   
    setFilters(newFilters || []);
  };

  const onApplyFilters = (newFilters) => {
    const nextFilters = (newFilters || []).map((rule) => ({
      ...rule,
      source: rule?.source || "modal",
    }));

    if (enableInlineColumnFilters) {
      const nextInline = {};
      nextFilters.forEach((rule) => {
        if (!rule?.columnId) return;
        if (rule.operator && rule.operator !== "contains") return;
        nextInline[rule.columnId] = rule.value ?? "";
      });
      setInlineColumnFilters(nextInline);
    }

    setFilters(nextFilters);

    // Notify parent (if provided) with current pagination & new filters
    if (typeof onDataRefresh === "function") {
      const payload = {
        filters: nextFilters,
        page: page ?? 0,
        pageSize: pageSize ?? 10,
      };

      try {
        onDataRefresh(payload);
      } catch (e) {
        console.error("onDataRefresh threw:", e);
      }
    }
  };


  const clearFilters = () => {
    if (inlineFilterTimerRef.current) clearTimeout(inlineFilterTimerRef.current);
    setInlineColumnFilters({});
    setFilters([]);
    if (typeof onDataRefresh === "function") {
      try {
        onDataRefresh({
          filters: [],
          page: 0,
          pageSize: pageSize ?? 10,
        });
      } catch (e) {
        console.error("onDataRefresh threw:", e);
      }
    }
  };

  React.useEffect(() => {
    if (enableDateRangeFilter) {
      setDateRange(defaultDateRange);
    }
  }, [defaultDateRange, enableDateRangeFilter]);

  const handleDateChange = (key, value) => {
    const updated = { ...dateRange, [key]: value };
    setDateRange(updated);
    if (onDateRangeChange) onDateRangeChange(updated);
  };


  // CommonDataTable: pagination change handler (calls parent onDataRefresh)
  // const handlePaginationModelChange = (newModel) => {
  //   setPaginationModel(newModel);

  //   if (typeof onDataRefresh === "function") {
  //     const payload = {
  //       filters: filters || [],
  //       page: newModel.page ?? 0,
  //       pageSize: newModel.pageSize ?? pageSize,
  //     };
  //     console.log("CommonDataTable -> page changed, calling onDataRefresh with", payload);
  //     try {
  //       onDataRefresh(payload);
  //     } catch (e) {
  //       console.error("onDataRefresh threw:", e);
  //     }
  //   }
  // };


  // Server pagination only when parent provides both handlers; otherwise paginate client-side
  // (the old initialRows.length > pageSize heuristic broke server lists with exactly pageSize rows)
  const isClientPagination = React.useMemo(() => {
    const hasServerHandlers =
      typeof onPageChange === "function" && typeof onPageSizeChange === "function";
    return !hasServerHandlers;
  }, [onPageChange, onPageSizeChange]);




  return (
    <Box className="cdt-root" sx={{ width: "100%", pl: 1 }} id={id}>
      <Box className="cdt-toolbar">
        {/* Create Button - First in order */}
        {showCreateButton && onCreateClick && (
          <Button
            variant={createButtonVariant}
            color={createButtonColor}
            onClick={onCreateClick}
            className="cdt-btn cdt-btn-primary"
          >
            <i className={`${createButtonIcon.startsWith("bi ") ? createButtonIcon : `bi ${createButtonIcon}`}`}></i>
            {createButtonText}
          </Button>
        )}

        {/* Import Button */}
        {!forItems && enableImportButton && (
          <Button variant="outlined" onClick={openUploadModal} className="cdt-btn cdt-btn-soft">
            <i className="bi bi-upload"></i>
            {t("commonTable.import")}
          </Button>
        )}

        {/* Export Button */}
        {!forItemsexport && enableExportButton && (
          <Button variant="outlined" onClick={handleExport} className="cdt-btn cdt-btn-soft">
            <i className="bi bi-download"></i>
            {t("commonTable.export")}
          </Button>
        )}

        <span className="cdt-divider" aria-hidden />

        {/* Filter Button */}
        <Tooltip title="Filters">
          <IconButton
            onClick={openFilterModal}
            aria-label="Filters"
            className={`cdt-icon-btn ${filters.length ? "is-active" : ""}`}
          >
            <Badge badgeContent={filters.length} color="error">
              <FilterListIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Clear filters quick action (only when active) */}
        {filters.length > 0 && (
          <Button variant="text" size="small" onClick={clearFilters} className="cdt-btn cdt-btn-link">
            Clear filters
          </Button>
        )}

        {/* Column Selector Button */}
        <Button variant="outlined" onClick={() => setOpen(true)} className="cdt-btn cdt-btn-ghost">
          <i className="bi bi-layout-three-columns"></i>
          {t("commonTable.columns")}
        </Button>

        {enableDateRangeFilter && (
          <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={dateRange.start}
              onChange={(e) => {
                const selectedStart = e.target.value;
                const today = new Date().toISOString().split("T")[0];
                if (selectedStart > today) return;
                if (dateRange.end && selectedStart > dateRange.end) return;
                setDateRange((prev) => ({ ...prev, start: selectedStart }));
              }}
              InputLabelProps={{ shrink: true }}
              max={dateRange.end || new Date().toISOString().split("T")[0]} // start <= end
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={dateRange.end}
              onChange={(e) => {
                const selectedEnd = e.target.value;
                const today = new Date().toISOString().split("T")[0];
                if (selectedEnd > today) return;
                if (dateRange.start && selectedEnd < dateRange.start) return;
                setDateRange((prev) => ({ ...prev, end: selectedEnd }));
              }}
              InputLabelProps={{ shrink: true }}
              max={new Date().toISOString().split("T")[0]} // end <= today
              min={dateRange.start} // end >= start
            />
            <Button
              variant="contained"
              onClick={() => {
                if (onDateRangeChange) onDateRangeChange(dateRange);
              }}
            >
              Go
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const oneMonthAgo = new Date(today);
                oneMonthAgo.setMonth(today.getMonth() - 1);
                const formatLocalDate = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  return `${year}-${month}-${day}`;
                };
                const reset = {
                  start: formatLocalDate(oneMonthAgo),
                  end: formatLocalDate(today),
                };
                setDateRange(reset);
                if (onDateRangeChange) onDateRangeChange(reset);
              }}
            >
              Reset
            </Button>
          </Box>
        )}

      
        {(showSyncWms || forInventory) && (
          <Box className="cdt-toolbar-end">
            {showSyncWms && (
              <Box
                component="span"
                onClick={() => {
                  if (syncCoolingDown && !syncing) toast.info(t("commonTable.syncWmsWait"));
                }}
                sx={syncCoolingDown ? { display: "inline-flex", cursor: "not-allowed" } : { display: "inline-flex" }}
              >
              <Button
                variant="outlined"
                onClick={handleSyncWms}
                disabled={syncing || syncCoolingDown}
                className="cdt-btn cdt-btn-soft"
                aria-label={t("commonTable.syncWms")}
                sx={syncCoolingDown ? { pointerEvents: "none" } : undefined}
              >
                {syncing ? (
                  <CircularProgress size={14} />
                ) : (
                  <i className="bi bi-arrow-repeat" />
                )}
                {t("commonTable.syncWms")}
              </Button>
              </Box>
            )}

            {forInventory && (
              <Box className="cdt-refresh">
                <span className="cdt-refresh-dot" aria-hidden />
                <span className="cdt-refresh-label">{t("commonTable.lastRefreshed")}</span>
                <span className="cdt-refresh-time">
                  {lastRefreshed ? new Date(lastRefreshed).toLocaleString([], { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton
                      onClick={handleManualRefresh}
                      disabled={refreshing}
                      aria-label="Refresh"
                      className="cdt-refresh-btn"
                    >
                      {refreshing ? (
                        <CircularProgress size={14} />
                      ) : (
                        <RefreshIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}


      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={displayedRows}
        columns={orderedColumns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        disableColumnMenu={Boolean(forInventory)}
        rowHeight={rowHeight}
        columnHeaderHeight={headerHeight}
        rowBuffer={10}
        rowThreshold={15}
        pageSizeOptions={rowsPerPageOptions}
        getRowClassName={getRowClassName}

        onRowSelectionModelChange={(newSelection) => {
          let values = [];
          for (let i of newSelection?.ids || []) {
            values.push(i);
          }
          setSelectedRows({ values });
          if (typeof onSelectionChange === "function") {
            const idSet = new Set(values.map((v) => String(v)));
            const selectedFullRows = (displayedRows || []).filter((r) =>
              idSet.has(String(r.id))
            );
            onSelectionChange({ ids: values, rows: selectedFullRows });
          }
        }}

        className="cdt-grid"
        sx={{
          border: "1px solid #e6edf5",
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(30, 57, 145, 0.06)",
          "--DataGrid-rowBorderColor": "#eef2f7",
          "--DataGrid-containerBackground": "#f7f9fc",
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller--header": {
            backgroundColor: "#f7f9fc",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "1px solid #e6edf5",
          },
          "& .MuiDataGrid-columnSeparator": {
            color: "#e2e8f0",
          },
          "& .MuiDataGrid-row": {
            transition: "background-color 0.18s ease, box-shadow 0.18s ease",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "#fbfcfe",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f3f8fd",
            boxShadow: "inset 3px 0 0 #1e71bb",
          },
          "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "rgba(30, 113, 187, 0.08)",
            boxShadow: "inset 3px 0 0 #2b3991",
          },
          "& .MuiDataGrid-cell": {
            fontSize: "13px",
            color: "#1f2937",
            borderColor: "#eef2f7",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
          "& .MuiCheckbox-root": {
            color: "#94a3b8",
          },
          "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": {
            color: "#1e71bb",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: "42px !important",
            maxHeight: "42px !important",
            height: "42px !important",
            overflow: "hidden !important",
            borderTop: "1px solid #e6edf5",
            backgroundColor: "#fbfcfe",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { height: 8, width: 8 },
          "& .MuiDataGrid-virtualScroller": {
            scrollbarColor: "#2b3991 #eef1f7",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            background: "linear-gradient(180deg, #2b3991, #1c2468)",
            borderRadius: 999,
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
            background: "linear-gradient(180deg, #3a48b4, #2b3991)",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": { background: "#eef1f7", borderRadius: 999 },
          "& .MuiTablePagination-root": {
            minHeight: "42px !important",
            maxHeight: "42px !important",
            height: "42px !important",
            overflow: "hidden !important",
          },
          "& .MuiTablePagination-toolbar": {
            minHeight: "42px !important",
            height: "42px !important",
            padding: "0px !important",
          },
          "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
            margin: 0,
            lineHeight: "42px !important",
          },
          "& .MuiTablePagination-select": {
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
            lineHeight: "42px !important",
          },
          "& .MuiButtonBase-root": { padding: "4px !important" },
          "& .MuiDataGrid-columnHeader": {
            overflow: "visible",
            "&:hover": { backgroundColor: "#eef3f9" },
          },
          "& .MuiDataGrid-columnHeaderTitle": { overflow: "visible" },
          "& .MuiDataGrid-columnHeader[data-field]": {
            transition: "all 0.2s ease",
          },
        }}

        /* 🔥 THE REAL FIX STARTS HERE */

        paginationMode={isClientPagination ? "client" : "server"}

        paginationModel={
          isClientPagination
            ? clientPaginationModel
            : { page: page ?? 0, pageSize: pageSize ?? 10 }
        }

        onPaginationModelChange={(model) => {
          if (isClientPagination) {
            setClientPaginationModel(model);   // DataGrid controls itself
            return;
          }

          if (model.page !== page && typeof onPageChange === "function") {
            onPageChange(model.page);
          }
          if (model.pageSize !== pageSize && typeof onPageSizeChange === "function") {
            onPageSizeChange(model.pageSize);
          }
        }}

        rowCount={isClientPagination ? displayedRows.length : rowCount}
      />


      {/* Filter Modal */}
      <FilterModal
        open={filterModalOpen}
        onClose={closeFilterModal}
        columns={(allPossibleColumns || []).filter(
          (col) => col?.field && col.filterable !== false && col.field !== "__actions"
        )}
        existingFilters={filters}
        onApply={onApplyFilters}
      />

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDialog}
        PaperProps={{ className: "cdt-columns-dialog" }}
      >
        <div className="cdt-columns-head">
          <div>
            <p className="cdt-columns-kicker">Table</p>
            <h3>Column preferences</h3>
            <p>Choose the fields on this list. Drag a column header in the table to change the order.</p>
          </div>
          <button type="button" className="cdt-columns-close" onClick={handleCloseDialog} aria-label="Close column preferences">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="cdt-columns-body">
          <div className="cdt-columns-meta">
            <span>{visibleColumnCount} of {(allPossibleColumns || []).length} visible</span>
            <button type="button" onClick={resetColumnOrder}>Reset order</button>
          </div>

          <label className="cdt-columns-search">
            <i className="bi bi-search" aria-hidden />
            <input
              type="text"
              placeholder="Search columns"
              value={columnSearchTerm}
              onChange={(e) => setColumnSearchTerm(e.target.value)}
            />
            {columnSearchTerm ? (
              <button type="button" onClick={() => setColumnSearchTerm("")} aria-label="Clear search">
                <i className="bi bi-x-lg" />
              </button>
            ) : null}
          </label>
          {columnSearchTerm ? (
            <p className="cdt-columns-found">
              {filteredColumns.length} of {(allPossibleColumns || []).length} columns
            </p>
          ) : null}

          <div className="cdt-columns-list">
            {preferenceColumns.length === 0 ? (
              <p className="cdt-columns-empty">No columns match “{columnSearchTerm}”.</p>
            ) : (
              preferenceColumns.map((col, index) => {
                const shown = columnVisibilityModel[col.field] ?? false;
                return (
                  <label key={col.field} className={`cdt-columns-row${shown ? " is-on" : ""}`}>
                    <Checkbox
                      checked={shown}
                      size="small"
                      onChange={(e) =>
                        setColumnVisibilityModel((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }))
                      }
                    />
                    <span className="cdt-columns-copy">
                      <span className="cdt-columns-name">{col.headerName}</span>
                      {columnSearchTerm ? <span className="cdt-columns-field">{col.field}</span> : null}
                    </span>
                    <span className="cdt-columns-index">{index + 1}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="cdt-columns-foot">
          <div className="cdt-columns-bulk">
            <button
              type="button"
              disabled={filteredColumns.length === 0}
              onClick={() => {
                const updatedModel = { ...columnVisibilityModel };
                filteredColumns.forEach((col) => {
                  updatedModel[col.field] = true;
                });
                setColumnVisibilityModel(updatedModel);
              }}
            >
              Show all{columnSearchTerm ? " found" : ""}
            </button>
            <button
              type="button"
              disabled={filteredColumns.length === 0}
              onClick={() => {
                const updatedModel = { ...columnVisibilityModel };
                filteredColumns.forEach((col) => {
                  updatedModel[col.field] = false;
                });
                setColumnVisibilityModel(updatedModel);
              }}
            >
              Hide all{columnSearchTerm ? " found" : ""}
            </button>
          </div>
          <div className="cdt-columns-actions">
            <button
              type="button"
              onClick={() => {
                const cols = Array.isArray(allPossibleColumns) ? allPossibleColumns : [];
                const visible = Array.isArray(initialVisible) ? initialVisible : [];
                setColumnVisibilityModel(Object.fromEntries(cols.map((c) => [c.field, visible.includes(c.field)])));
                setColumnSearchTerm("");
              }}
            >
              Reset
            </button>
            <button type="button" className="is-primary" onClick={handleCloseDialog}>
              Done
            </button>
          </div>
        </div>
      </Drawer>

      {/* File Upload Modal */}
      <FileUploadModal
        show={showUploadModal}
        onHide={closeUploadModal}
        onFileUpload={uploadHandler}
        onDownloadSample={handleDownloadSample}
        title="Import Data"
        sampleFileName={computedSampleFileName}
        acceptedFileTypes=".xlsx,.xls,.csv"
        uploadPath={exportImportPath}
      />

      {/* Validation Error Modal */}
      <ValidationErrorModal show={showErrorModal} onHide={hideValidationErrors} errors={validationErrors} />
    </Box>
  );
}
